import type { PublishedProject } from "../portfolio/types";

export const SITE_ORIGIN = "https://clelandmason.com";
export const SITE_NAME = "Mason Cleland";

const MAX_DESCRIPTION_LENGTH = 160;

export interface PageMetadata {
  path: string;
  title: string;
  description: string;
  canonicalUrl: string | null;
  robots: "index,follow" | "noindex,follow";
  ogType: "website" | "article";
}

export const HOME_METADATA: PageMetadata = {
  path: "/",
  title: "Mason Cleland | Software & Systems Engineer",
  description:
    "Mason Cleland is a software engineer and U.S. Space Force veteran building software, Linux systems, automation, and hardware-integrated workflows.",
  canonicalUrl: `${SITE_ORIGIN}/`,
  robots: "index,follow",
  ogType: "website",
};

export const RESUME_METADATA: PageMetadata = {
  path: "/resume",
  title: "Resume | Mason Cleland",
  description:
    "Resume for Mason Cleland, a software engineer and U.S. Space Force veteran with software, systems, DevSecOps, geospatial, and SATCOM experience.",
  canonicalUrl: `${SITE_ORIGIN}/resume`,
  robots: "index,follow",
  ogType: "website",
};

/** Normalizes a route pathname to the canonical no-trailing-slash form used by the portfolio. */
function normalizePathname(pathname: string): string {
  if (!pathname || pathname === "/") {
    return "/";
  }

  return `/${pathname.replace(/^\/+|\/+$/g, "")}`;
}

/** Produces a concise metadata description without splitting the final visible word when truncation is required. */
function metadataDescription(value: string): string {
  const normalized = value.replace(/\s+/g, " ").trim();

  if (normalized.length <= MAX_DESCRIPTION_LENGTH) {
    return normalized;
  }

  const candidate = normalized.slice(0, MAX_DESCRIPTION_LENGTH - 1);
  const finalSpace = candidate.lastIndexOf(" ");
  const body =
    finalSpace > MAX_DESCRIPTION_LENGTH * 0.65
      ? candidate.slice(0, finalSpace)
      : candidate;

  return `${body.trimEnd()}…`;
}

/** Returns the canonical public URL for one known application route. */
export function canonicalUrlForPath(pathname: string): string {
  const normalized = normalizePathname(pathname);
  return normalized === "/" ? `${SITE_ORIGIN}/` : `${SITE_ORIGIN}${normalized}`;
}

/** Builds recruiter- and crawler-facing metadata from one validated published project projection. */
export function metadataForProject(project: PublishedProject): PageMetadata {
  const path = `/projects/${project.id}`;
  const description =
    project.sourceType === "public-overview"
      ? project.purpose[0]
      : project.summary;

  return {
    path,
    title: `${project.title} | Mason Cleland`,
    description: metadataDescription(description),
    canonicalUrl: canonicalUrlForPath(path),
    robots: "index,follow",
    ogType: "article",
  };
}

/** Resolves page metadata for browser navigation, returning a no-index projection for unknown routes. */
export function metadataForPath(
  pathname: string,
  projects: readonly PublishedProject[],
): PageMetadata {
  const normalized = normalizePathname(pathname);

  if (normalized === "/") {
    return HOME_METADATA;
  }

  if (normalized === "/resume") {
    return RESUME_METADATA;
  }

  const projectPrefix = "/projects/";
  if (normalized.startsWith(projectPrefix)) {
    const projectId = normalized.slice(projectPrefix.length);
    const project = projects.find((candidate) => candidate.id === projectId);

    if (project) {
      return metadataForProject(project);
    }
  }

  return {
    path: normalized,
    title: "Page Not Found | Mason Cleland",
    description:
      "The requested page is not part of Mason Cleland's published engineering portfolio.",
    canonicalUrl: null,
    robots: "noindex,follow",
    ogType: "website",
  };
}
